import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../store/reducers/snackbarSlice';
import { Company } from '../../../types';
import {
  CompanyGuestCall,
  CompanyWidget,
  createCompanyTablet,
  getCompanyGuestCalls,
  getCompanyWidget,
} from '../companyHelpers';
import { nfcAgent, NfcAgentState } from '../../../utils/nfcAgent';
import style from './TabletQrModal.module.css';

interface TabletQrModalProps {
  /** §FB37: вкладка сторінки компанії — без оверлею та кнопки закриття. */
  embedded?: boolean;
  companyId: number;
  company?: Company;
  onClose: () => void;
}

const NfcGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 8.5a8 8 0 0 1 0 7" />
    <path d="M9.5 6a13 13 0 0 1 0 12" />
    <path d="M13 4a17 17 0 0 1 0 16" />
    <circle cx="4" cy="12" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);
const CopyGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const DownloadGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
// §FB169: значок «людина» тут стояв при попередженні «немає відповідальної особи».
// Попередження знято разом із самою вимогою, тож і значок пішов.
const NfcWriteGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 8.5a8 8 0 0 1 0 7" />
    <path d="M9.5 6a13 13 0 0 1 0 12" />
    <path d="M13 4a17 17 0 0 1 0 16" />
    <circle cx="4" cy="12" r="0.7" fill="currentColor" stroke="none" />
  </svg>
);
const EraseGlyph = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

/**
 * Малює NFC-іконку (безконтактні хвилі) у центрі QR-коду таблички та повертає data-URL готового PNG.
 * QR приходить з бекенду з високою корекцією помилок (ECC H), тож центральна іконка не заважає скануванню.
 */
const composeQrWithNfc = (qrBase64: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const size = img.naturalWidth || 512;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('no ctx'));
        ctx.drawImage(img, 0, 0, size, size);

        const cx = size / 2;
        const cy = size / 2;
        const box = Math.round(size * 0.26);
        const x = cx - box / 2;
        const y = cy - box / 2;
        const r = Math.round(box * 0.24);

        // Біле скруглене «вікно» під іконкою (щоб не зливалося з модулями QR).
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + box, y, x + box, y + box, r);
        ctx.arcTo(x + box, y + box, x, y + box, r);
        ctx.arcTo(x, y + box, x, y, r);
        ctx.arcTo(x, y, x + box, y, r);
        ctx.closePath();
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.lineWidth = Math.max(2, size * 0.006);
        ctx.strokeStyle = '#0a8081';
        ctx.stroke();

        // NFC/безконтактні хвилі (відкриті праворуч) + точка-джерело зліва.
        const stroke = Math.max(2, box * 0.06);
        ctx.strokeStyle = '#0a8081';
        ctx.fillStyle = '#0a8081';
        ctx.lineWidth = stroke;
        ctx.lineCap = 'round';
        const originX = cx - box * 0.17;
        const start = -Math.PI / 3.2;
        const end = Math.PI / 3.2;
        [box * 0.15, box * 0.26, box * 0.37].forEach((rr) => {
          ctx.beginPath();
          ctx.arc(originX, cy, rr, start, end);
          ctx.stroke();
        });
        ctx.beginPath();
        ctx.arc(originX, cy, stroke * 0.7, 0, Math.PI * 2);
        ctx.fill();

        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        reject(e as Error);
      }
    };
    img.onerror = () => reject(new Error('img load failed'));
    img.src = `data:image/png;base64,${qrBase64}`;
  });

// §FB40: дата/час гостьового виклику в київському поясі: «08.07.2026, 19:14».
const formatCallAt = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString('uk-UA', {
      timeZone: 'Europe/Kyiv',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d.toLocaleString('uk-UA');
  }
};

const formatCallDuration = (secs?: number | null): string => {
  if (!secs || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const sRest = secs % 60;
  return m > 0 ? `${m} хв ${String(sRest).padStart(2, '0')} с` : `${sRest} с`;
};

// Бейдж результату виклику (outcome рахує бекенд).
const OUTCOME_VIEW: Record<string, { label: string; tone: 'ok' | 'live' | 'bad' }> = {
  finished: { label: 'Відбувся', tone: 'ok' },
  active: { label: 'Триває', tone: 'live' },
  ringing: { label: 'Дзвонить…', tone: 'live' },
  canceled: { label: 'Відхилено', tone: 'bad' },
  missed: { label: 'Без відповіді', tone: 'bad' },
};

const TabletQrModal: React.FC<TabletQrModalProps> = ({
  companyId,
  company,
  onClose,
  embedded = false,
}) => {
  const dispatch = useDispatch();
  const [widget, setWidget] = useState<CompanyWidget | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  // §FB40: реєстр гостьових викликів організації (нижче блоку посилання/коду).
  const [calls, setCalls] = useState<CompanyGuestCall[] | null>(null);
  const [callsLoading, setCallsLoading] = useState<boolean>(false);
  const [composed, setComposed] = useState<string | null>(null);
  const [nfc, setNfc] = useState<NfcAgentState>(nfcAgent.getState());
  const [writing, setWriting] = useState<boolean>(false);
  const [erasing, setErasing] = useState<boolean>(false);
  const [writeMsg, setWriteMsg] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const companyName = company?.name;

  // Live connection to the local NFC agent while this modal is open.
  useEffect(() => {
    nfcAgent.acquire();
    const unsub = nfcAgent.subscribe(setNfc);
    return () => {
      unsub();
      nfcAgent.cancel();
      nfcAgent.release();
    };
  }, []);

  const handleWriteNfc = async () => {
    if (writing || erasing || !widget?.tabletUrl) return;
    setWriteMsg(null);
    setWriting(true);
    const res = await nfcAgent.write(widget.tabletUrl);
    setWriting(false);
    if (res.ok) {
      setWriteMsg({ ok: true, text: `Записано на ${res.tag} ✓` });
      dispatch(
        showSnackbar({
          message: 'Посилання записано на NFC-мітку',
          type: 'success',
        }),
      );
    } else {
      setWriteMsg({ ok: false, text: res.message });
      dispatch(showSnackbar({ message: res.message, type: 'error' }));
    }
  };

  const handleEraseNfc = async () => {
    if (writing || erasing) return;
    setWriteMsg(null);
    setErasing(true);
    const res = await nfcAgent.erase();
    setErasing(false);
    if (res.ok) {
      setWriteMsg({ ok: true, text: `Мітку стерто (${res.tag}) ✓` });
      dispatch(showSnackbar({ message: 'NFC-мітку стерто', type: 'success' }));
    } else {
      setWriteMsg({ ok: false, text: res.message });
      dispatch(showSnackbar({ message: res.message, type: 'error' }));
    }
  };

  const load = () => {
    setLoading(true);
    setError(false);
    return getCompanyWidget(companyId)
      .then((data) => {
        setWidget(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getCompanyWidget(companyId)
      .then((data) => {
        if (!active) return;
        setWidget(data);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId]);

  // Композиція QR + NFC-іконки, коли з'явився QR таблички.
  useEffect(() => {
    let active = true;
    const b64 = widget?.tabletQrPngBase64;
    if (!b64) {
      setComposed(null);
      return;
    }
    composeQrWithNfc(b64)
      .then((url) => {
        if (active) setComposed(url);
      })
      .catch(() => {
        // Фолбек — «голий» QR без іконки.
        if (active) setComposed(`data:image/png;base64,${b64}`);
      });
    return () => {
      active = false;
    };
  }, [widget?.tabletQrPngBase64]);

  const copyUrl = (url: string) => {
    const notify = (ok: boolean) =>
      dispatch(
        showSnackbar({
          message: ok
            ? 'Посилання скопійовано'
            : 'Не вдалося скопіювати. Виділіть поле та натисніть Ctrl+C',
          type: ok ? 'success' : 'error',
        }),
      );
    const legacyCopy = (): boolean => {
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '0';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, url.length);
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
      } catch {
        return false;
      }
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url).then(
        () => notify(true),
        () => notify(legacyCopy()),
      );
    } else {
      notify(legacyCopy());
    }
  };

  const handleDownload = () => {
    const href =
      composed ||
      (widget?.tabletQrPngBase64
        ? `data:image/png;base64,${widget.tabletQrPngBase64}`
        : null);
    if (!href) return;
    const safeName = (widget?.companyName || companyName || 'company')
      .replace(/[\\/:*?"<>|]+/g, '_')
      .trim()
      .slice(0, 60);
    const link = document.createElement('a');
    link.href = href;
    link.download = `QR_NFC_${safeName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // §FB40: тягнемо реєстр при відкритті вкладки; кнопка «оновити» — той самий шлях.
  const loadCalls = async () => {
    setCallsLoading(true);
    try {
      setCalls(await getCompanyGuestCalls(companyId));
    } catch {
      setCalls([]);
    } finally {
      setCallsLoading(false);
    }
  };

  useEffect(() => {
    loadCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      // §FB169: створюємо саме ТАБЛИЧКУ. Раніше тут стояв createCompanyWidget — і через
      // це вкладка таблички успадковувала чужу передумову (відповідальна особа), хоча
      // її власний сценарій гостьовий і відповідального не потребує.
      const fresh = await createCompanyTablet(companyId);
      setWidget(fresh);
      dispatch(
        showSnackbar({ message: 'Посилання створено', type: 'success' }),
      );
    } catch (e: any) {
      const msg =
        e?.response?.data?.Message ||
        e?.response?.data?.message ||
        'Не вдалося створити посилання';
      dispatch(showSnackbar({ message: msg, type: 'error' }));
      load();
    } finally {
      setCreating(false);
    }
  };

  // §FB169: ознака — ВЛАСНИЙ лінк таблички, а не наявність віджета. Прив'язка до
  // widget.exists ховала готову табличку в компаніях без віджета.
  const created =
    !!widget?.tabletExists && !!widget.tabletUrl && !!widget.tabletQrPngBase64;

  // Live NFC tag state (from the agent). Writing is allowed ONLY to an empty tag;
  // a tag that already carries a record must be erased first.
  const hasCard = !!nfc.card;
  const tagInfo = nfc.card?.ndef ?? null;
  const tagState = tagInfo?.state; // 'empty' | 'written' | 'unreadable' | undefined
  const inspecting = hasCard && !tagInfo; // card present, not yet read
  const writeDisabled = writing || erasing || (hasCard && tagState !== 'empty');

  return (
    <div
      className={embedded ? style.embeddedWrap : style.modalOverlay}
      onClick={embedded ? undefined : onClose}
    >
      <div
        className={`${style.modal} ${created ? '' : style.modalNarrow} ${embedded ? style.modalEmbedded : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {!embedded && (
          <div className={style.header}>
            <h5 className={style.title}>QR та NFC для таблички</h5>
            {!embedded && (
              <button
                className={style.closeButton}
                onClick={onClose}
                aria-label="Закрити"
              >
                ×
              </button>
            )}
          </div>
        )}

        <div className={style.body}>
          {companyName && !created && (
            <p className={style.company}>{companyName}</p>
          )}

          {loading && <p className={style.muted}>Завантаження…</p>}
          {error && (
            <p className={style.errorText}>
              Не вдалося завантажити. Спробуйте ще раз.
            </p>
          )}

          {widget && !loading && !error && created && (
            <>
            <div className={style.createdRow}>
              <div className={style.qrCol}>
                <div className={style.qrWrap}>
                  {composed ? (
                    <img
                      className={style.qr}
                      src={composed}
                      alt="QR-код для таблички з NFC-іконкою"
                    />
                  ) : (
                    <div className={style.qrPlaceholder} />
                  )}
                </div>
                <p className={style.qrHint}>
                  Код для друку на табличці. У центрі — позначка NFC.
                </p>
              </div>

              <div className={style.vDivider} />

              <div className={style.infoCol}>
                {companyName && <p className={style.company}>{companyName}</p>}
                <p className={style.subhint}>
                  Постійне посилання для таблички з кодом і NFC
                </p>

                <label className={style.urlLabel}>
                  Посилання (для QR та запису на NFC-мітку)
                </label>
                <input
                  className={style.urlField}
                  type="text"
                  readOnly
                  value={widget.tabletUrl}
                  onFocus={(e) => e.currentTarget.select()}
                />

                <div className={style.actionsRow}>
                  <button className={style.primaryBtn} onClick={handleDownload}>
                    <DownloadGlyph className={style.btnIcon} />
                    <span>Завантажити QR</span>
                  </button>
                  <button
                    className={style.secondaryBtn}
                    onClick={() => copyUrl(widget.tabletUrl!)}
                  >
                    <CopyGlyph className={style.btnIcon} />
                    <span>Копіювати посилання</span>
                  </button>
                </div>

                <p className={style.note}>
                  Це саме посилання записується і на NFC-мітку таблички: люди
                  зможуть відкрити виклик перекладача як скануванням коду, так і
                  дотиком телефону.
                </p>

                <div className={style.nfcSection}>
                  {!nfc.connected && (
                    <p className={style.nfcHelp}>
                      Щоб записати посилання просто на NFC-мітку, запустіть на
                      цьому комп'ютері застосунок <b>WordCraft NFC Agent</b> і
                      під'єднайте зчитувач ACR1252.
                    </p>
                  )}
                  {nfc.connected && !nfc.reader && (
                    <div className={style.nfcRow}>
                      <span className={`${style.nfcDot} ${style.nfcOff}`} />
                      <span className={style.nfcStatus}>
                        Під'єднайте зчитувач ACR1252 до USB…
                      </span>
                    </div>
                  )}
                  {nfc.connected && nfc.reader && (
                    <>
                      {/* What's currently on the tag (read on tap by the agent). */}
                      <div className={style.nfcTagBox}>
                        {!hasCard && (
                          <span className={style.nfcTagMuted}>
                            Піднесіть мітку до зчитувача, щоб побачити її вміст…
                          </span>
                        )}
                        {inspecting && (
                          <span className={style.nfcTagMuted}>
                            Зчитування мітки…
                          </span>
                        )}
                        {tagState === 'empty' && (
                          <span className={style.nfcTagEmpty}>
                            ✓ Мітка порожня — можна записувати
                          </span>
                        )}
                        {tagState === 'written' && (
                          <div className={style.nfcTagWritten}>
                            <span className={style.nfcTagLabel}>
                              На мітці вже записано:
                            </span>
                            {tagInfo?.uri ? (
                              <a
                                className={style.nfcTagUri}
                                href={tagInfo.uri}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {tagInfo.uri}
                              </a>
                            ) : (
                              <span className={style.nfcTagUri}>
                                невідомий запис (не посилання)
                              </span>
                            )}
                            <span className={style.nfcTagSub}>
                              Щоб перезаписати — спершу натисніть «Стерти
                              мітку».
                            </span>
                          </div>
                        )}
                        {tagState === 'unreadable' && (
                          <span className={style.nfcTagErr}>
                            Не вдалося прочитати мітку (можливо, чужа картка з
                            власними ключами). Візьміть чисту NTAG213/215/216.
                          </span>
                        )}
                      </div>

                      <div className={style.nfcRow}>
                        <button
                          className={style.nfcBtn}
                          onClick={handleWriteNfc}
                          disabled={writeDisabled}
                          title={
                            hasCard && tagState !== 'empty'
                              ? 'Запис дозволено лише на порожню мітку — спершу зітріть її'
                              : undefined
                          }
                        >
                          <NfcWriteGlyph className={style.btnIcon} />
                          <span>
                            {writing
                              ? nfc.card
                                ? 'Запис…'
                                : 'Піднесіть мітку…'
                              : 'Записати на NFC-мітку'}
                          </span>
                        </button>
                        <button
                          className={style.nfcEraseBtn}
                          onClick={handleEraseNfc}
                          disabled={writing || erasing}
                          title="Стерти вміст мітки (форматувати в порожню NFC-мітку)"
                        >
                          <EraseGlyph className={style.btnIcon} />
                          <span>
                            {erasing
                              ? nfc.card
                                ? 'Стирання…'
                                : 'Піднесіть мітку…'
                              : 'Стерти мітку'}
                          </span>
                        </button>
                        <span className={style.nfcReady}>
                          <span className={`${style.nfcDot} ${style.nfcOn}`} />
                          Зчитувач готовий
                        </span>
                      </div>
                      {writeMsg && (
                        <p
                          className={`${style.nfcStatus} ${
                            writeMsg.ok ? style.nfcOk : style.nfcErr
                          }`}
                        >
                          {writeMsg.text}
                        </p>
                      )}
                      <p className={style.nfcEraseHint}>
                        Записати можна лише на порожню мітку. «Стерти мітку»
                        очищає будь-яке попереднє посилання — після цього мітку
                        можна записати заново.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* §FB40: реєстр гостьових викликів цієї організації — під блоком посилання/коду. */}
            <div className={style.callsSection}>
              <div className={style.callsHead}>
                <p className={style.callsTitle}>Гостьові виклики</p>
                {calls && calls.length > 0 && (
                  <span className={style.callsCount}>{calls.length}</span>
                )}
                <button
                  type="button"
                  className={style.callsRefresh}
                  onClick={loadCalls}
                  disabled={callsLoading}
                  title="Оновити список викликів"
                  aria-label="Оновити список викликів"
                >
                  ⟳
                </button>
              </div>
              {!calls && callsLoading && (
                <p className={style.callsMuted}>Завантаження…</p>
              )}
              {calls && calls.length === 0 && (
                <p className={style.callsMuted}>
                  Викликів через QR/NFC-табличку ще не було.
                </p>
              )}
              {calls && calls.length > 0 && (
                <div className={style.callsList}>
                  <div className={`${style.callRow} ${style.callHeadRow}`}>
                    <span>Дата і час</span>
                    <span>Гість</span>
                    <span>Тривалість</span>
                    <span>Статус</span>
                  </div>
                  {calls.map((c) => {
                    const view = OUTCOME_VIEW[c.outcome] ?? OUTCOME_VIEW.missed;
                    const toneClass =
                      view.tone === 'ok'
                        ? style.callOk
                        : view.tone === 'live'
                          ? style.callLive
                          : style.callBad;
                    return (
                      <div key={c.id} className={style.callRow}>
                        <span className={style.callDate}>
                          {formatCallAt(c.createdAt)}
                        </span>
                        <span className={style.callGuest}>
                          {c.guestName || 'Гість'}
                        </span>
                        <span className={style.callDur}>
                          {formatCallDuration(c.durationSeconds)}
                        </span>
                        <span className={`${style.callBadge} ${toneClass}`}>
                          {view.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            </>
          )}

          {widget && !loading && !error && !created && (
            <div className={style.emptyState}>
              <span className={style.emptyIcon}>
                <NfcGlyph />
              </span>
              {/* §FB169: тут стояла розвилка «є відповідальна особа?», і без неї кнопка
                  ховалась. Це була чужа передумова: скан таблички відкриває 2-сторонній
                  гостьовий виклик, у якому відповідального не шукають узагалі. Тепер
                  табличку можна завести й компанії, де ще немає жодного співробітника. */}
              <p className={style.emptyTitle}>Посилання ще не створено</p>
              <p className={style.emptyNote}>
                Створіть постійне посилання та QR-код для таблички (з кодом і
                NFC). Після створення воно не змінюється. Відповідальна особа
                для таблички не потрібна — виклик іде напряму перекладачу.
              </p>
              <button
                className={style.createBtn}
                onClick={handleCreate}
                disabled={creating}
              >
                <NfcGlyph className={style.btnIcon} />
                <span>
                  {creating ? 'Створення…' : 'Створити посилання та QR'}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TabletQrModal;
